import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import {
  FaExternalLinkAlt, FaProjectDiagram, FaTasks, FaChartLine,
  FaShareAlt, FaUsers, FaClipboardList,
} from 'react-icons/fa';
import { getWorkspace, getWorkspaceSummary } from '@/api/workspaceApi';
import { getWorkItems } from '@/api/workItemsApi';
import type { Workspace, WorkspaceSummary } from '@/types/workspace';
import type { ProjectItem, KpiItem } from '@/types/workItem';

const healthColor = (h?: string) => {
  if (h === 'Green')  return 'success';
  if (h === 'Yellow') return 'warning';
  if (h === 'Red')    return 'danger';
  return 'secondary';
};

const kpiPct = (current?: number, target?: number): number => {
  if (!target || !current) return 0;
  return Math.min(100, Math.round((current / target) * 100));
};

const WorkspaceHome: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();

  const [workspace, setWorkspace]   = useState<Workspace | null>(null);
  const [summary, setSummary]       = useState<WorkspaceSummary | null>(null);
  const [projects, setProjects]     = useState<ProjectItem[]>([]);
  const [kpis, setKpis]             = useState<KpiItem[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) return;
    (async () => {
      try {
        const [ws, sum, rawProjects, rawKpis] = await Promise.all([
          getWorkspace(workspaceId),
          getWorkspaceSummary(workspaceId),
          getWorkItems(workspaceId, 'Projects', undefined, 10),
          getWorkItems(workspaceId, 'KPIs', undefined, 6),
        ]);
        setWorkspace(ws);
        setSummary(sum);
        setProjects(rawProjects as ProjectItem[]);
        setKpis(rawKpis as KpiItem[]);
      } catch {
        setError('Failed to load workspace. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [workspaceId]);

  if (loading) {
    return <Container className="py-5 text-center"><Spinner animation="border" /></Container>;
  }

  if (error || !workspace) {
    return (
      <Container className="py-4">
        <Alert variant="danger">{error ?? 'Workspace not found.'}</Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/workspaces')}>
          Back to workspaces
        </Button>
      </Container>
    );
  }

  return (
    <Container className="py-4" style={{ maxWidth: 1100 }}>
      {/* Header */}
      <div className="d-flex align-items-start justify-content-between mb-4">
        <div>
          <h2 className="fw-bold mb-1">{workspace.displayName}</h2>
          <p className="text-muted mb-0">{workspace.description}</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {workspace.sharePointSiteUrl && (
            <Button variant="outline-secondary" size="sm" href={workspace.sharePointSiteUrl} target="_blank" rel="noreferrer">
              <FaShareAlt className="me-1" /> SharePoint
            </Button>
          )}
          {workspace.teamsGroupId && (
            <Button
              variant="outline-secondary"
              size="sm"
              href={`https://teams.microsoft.com/l/team/${workspace.teamsGroupId}`}
              target="_blank"
              rel="noreferrer"
            >
              <FaUsers className="me-1" /> Teams
            </Button>
          )}
          {workspace.plannerPlanId && (
            <Button
              variant="outline-secondary"
              size="sm"
              href={`https://tasks.office.com/Home/Planner/#/plantaskboard?groupId=${workspace.teamsGroupId}&planId=${workspace.plannerPlanId}`}
              target="_blank"
              rel="noreferrer"
            >
              <FaClipboardList className="me-1" /> Planner
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <Row className="g-3 mb-4">
          <Col xs={12} md={4}>
            <Card className="border-0 shadow-sm h-100 text-center p-3">
              <div className="text-primary mb-1" style={{ fontSize: 32 }}><FaProjectDiagram /></div>
              <div style={{ fontSize: 36, fontWeight: 700 }}>{summary.activeProjectCount}</div>
              <div className="text-muted small">Active Projects</div>
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card className="border-0 shadow-sm h-100 text-center p-3">
              <div className="text-warning mb-1" style={{ fontSize: 32 }}><FaTasks /></div>
              <div style={{ fontSize: 36, fontWeight: 700 }}>{summary.openTaskCount}</div>
              <div className="text-muted small">Open Tasks</div>
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card className="border-0 shadow-sm h-100 text-center p-3">
              <div className="text-danger mb-1" style={{ fontSize: 32 }}><FaChartLine /></div>
              <div style={{ fontSize: 36, fontWeight: 700 }}>{summary.atRiskKpiCount}</div>
              <div className="text-muted small">KPIs at Risk</div>
            </Card>
          </Col>
        </Row>
      )}

      <Row className="g-4">
        {/* Projects */}
        <Col xs={12} lg={7}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
              <strong>Projects</strong>
              <Badge bg="secondary">{projects.length}</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {projects.length === 0 ? (
                <p className="text-muted small p-3 mb-0">No projects yet. Add one from the SharePoint Projects list.</p>
              ) : (
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Category</th>
                      <th style={{ width: 80 }}>Health</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((p, i) => (
                      <tr key={p.id ?? i}>
                        <td>{p.fields.Title}</td>
                        <td><Badge bg="secondary" className="fw-normal">{p.fields.Status ?? '—'}</Badge></td>
                        <td><small className="text-muted">{p.fields.Category ?? '—'}</small></td>
                        <td>
                          <span
                            className={`badge bg-${healthColor(p.fields.HealthIndicator)}`}
                            style={{ minWidth: 60 }}
                          >
                            {p.fields.HealthIndicator ?? '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card.Body>
            {workspace.sharePointSiteUrl && (
              <Card.Footer className="bg-white border-top text-end">
                <a
                  href={`${workspace.sharePointSiteUrl}/Lists/Projects`}
                  target="_blank"
                  rel="noreferrer"
                  className="small text-decoration-none"
                >
                  View all in SharePoint <FaExternalLinkAlt className="ms-1" />
                </a>
              </Card.Footer>
            )}
          </Card>
        </Col>

        {/* KPIs */}
        <Col xs={12} lg={5}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
              <strong>KPI Strip</strong>
              <Badge bg="secondary">{kpis.length}</Badge>
            </Card.Header>
            <Card.Body>
              {kpis.length === 0 ? (
                <p className="text-muted small mb-0">No KPIs yet. Add them from the SharePoint KPIs list.</p>
              ) : (
                kpis.map((k, i) => {
                  const pct = kpiPct(k.fields.Current, k.fields.Target);
                  const variant = k.fields.Status === 'On Track' ? 'success' : k.fields.Status === 'At Risk' ? 'warning' : 'danger';
                  return (
                    <div key={k.id ?? i} className="mb-3">
                      <div className="d-flex justify-content-between small mb-1">
                        <span className="fw-semibold">{k.fields.Title}</span>
                        <span className={`text-${variant}`}>
                          {k.fields.Current ?? '—'} / {k.fields.Target ?? '—'} {k.fields.Unit ?? ''}
                        </span>
                      </div>
                      <div className="progress" style={{ height: 6 }}>
                        <div
                          className={`progress-bar bg-${variant}`}
                          style={{ width: `${pct}%` }}
                          role="progressbar"
                          aria-valuenow={pct}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </Card.Body>
            {workspace.sharePointSiteUrl && (
              <Card.Footer className="bg-white border-top text-end">
                <a
                  href={`${workspace.sharePointSiteUrl}/Lists/KPIs`}
                  target="_blank"
                  rel="noreferrer"
                  className="small text-decoration-none"
                >
                  View all in SharePoint <FaExternalLinkAlt className="ms-1" />
                </a>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default WorkspaceHome;
